import { ethers } from 'ethers';

/**
 * Native token config per chain
 */
const NATIVE_TOKENS = {
  1: { name: 'Ethereum', symbol: 'ETH', icon: '💎', coingeckoId: 'ethereum' },
  11155111: { name: 'Sepolia ETH', symbol: 'ETH', icon: '💎', coingeckoId: 'ethereum' },
  5: { name: 'Goerli ETH', symbol: 'ETH', icon: '💎', coingeckoId: 'ethereum' },
  137: { name: 'Polygon', symbol: 'POL', icon: '🟣', coingeckoId: 'matic-network' },
  80002: { name: 'Polygon Amoy', symbol: 'POL', icon: '🟣', coingeckoId: 'matic-network' },
  42161: { name: 'Arbitrum ETH', symbol: 'ETH', icon: '💎', coingeckoId: 'ethereum' },
  10: { name: 'Optimism ETH', symbol: 'ETH', icon: '💎', coingeckoId: 'ethereum' },
  8453: { name: 'Base ETH', symbol: 'ETH', icon: '💎', coingeckoId: 'ethereum' },
  56: { name: 'BNB', symbol: 'BNB', icon: '💛', coingeckoId: 'binancecoin' },
  43114: { name: 'Avalanche', symbol: 'AVAX', icon: '🔺', coingeckoId: 'avalanche-2' },
};

/**
 * ERC-20 stablecoins per chain (chainId -> tokens)
 */
const ERC20_TOKENS_BY_CHAIN = {
  // Ethereum Mainnet
  1: {
    USDC: { name: 'USD Coin', symbol: 'USDC', icon: '💵', decimals: 6, address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', coingeckoId: 'usd-coin' },
    USDT: { name: 'Tether USD', symbol: 'USDT', icon: '💵', decimals: 6, address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', coingeckoId: 'tether' },
    DAI: { name: 'Dai', symbol: 'DAI', icon: '💵', decimals: 18, address: '0x6B175474E89094C44Da98b954EescdeCB5C811111', coingeckoId: 'dai' },
  },
  // Polygon Mainnet
  137: {
    USDC: { name: 'USD Coin', symbol: 'USDC', icon: '💵', decimals: 6, address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', coingeckoId: 'usd-coin' },
    USDT: { name: 'Tether USD', symbol: 'USDT', icon: '💵', decimals: 6, address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', coingeckoId: 'tether' },
    DAI: { name: 'Dai', symbol: 'DAI', icon: '💵', decimals: 18, address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', coingeckoId: 'dai' },
  },
  // Polygon Amoy Testnet
  80002: {
    USDC: { name: 'USD Coin (Test)', symbol: 'USDC', icon: '💵', decimals: 6, address: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582', coingeckoId: 'usd-coin' },
  },
  // Arbitrum One
  42161: {
    USDC: { name: 'USD Coin', symbol: 'USDC', icon: '💵', decimals: 6, address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', coingeckoId: 'usd-coin' },
    USDT: { name: 'Tether USD', symbol: 'USDT', icon: '💵', decimals: 6, address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', coingeckoId: 'tether' },
  },
  // Optimism
  10: {
    USDC: { name: 'USD Coin', symbol: 'USDC', icon: '💵', decimals: 6, address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', coingeckoId: 'usd-coin' },
    USDT: { name: 'Tether USD', symbol: 'USDT', icon: '💵', decimals: 6, address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', coingeckoId: 'tether' },
  },
  // Base
  8453: {
    USDC: { name: 'USD Coin', symbol: 'USDC', icon: '💵', decimals: 6, address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', coingeckoId: 'usd-coin' },
  },
  // BNB Chain
  56: {
    USDC: { name: 'USD Coin', symbol: 'USDC', icon: '💵', decimals: 18, address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', coingeckoId: 'usd-coin' },
    USDT: { name: 'Tether USD', symbol: 'USDT', icon: '💵', decimals: 18, address: '0x55d398326f99059fF775485246999027B3197955', coingeckoId: 'tether' },
  },
};

class WalletService {
  constructor() {
    this.provider = null;
    this.signer = null;
    this.network = null;
  }

  async getCurrentNetwork() {
    const network = await this.provider.getNetwork();
    return {
      chainId: Number(network.chainId),
      name: network.name,
    };
  }

  /**
   * Initialize with current wallet. Pass signer when using WalletConnect (e.g. from useWalletClient).
   * @param {import('ethers').Signer} [externalSigner] - optional; from getEthersSignerFromWalletClient(walletClient)
   */
  async initialize(externalSigner = null) {
    if (externalSigner) {
      this.signer = externalSigner;
      this.provider = externalSigner.provider;
    } else {
      if (typeof window === 'undefined' || !window.ethereum) {
        throw new Error('No wallet provider. Connect via RainbowKit first.');
      }
      this.provider = new ethers.BrowserProvider(window.ethereum);
      this.signer = await this.provider.getSigner();
    }
    this.network = await this.getCurrentNetwork();
    // eslint-disable-next-line no-console
    console.log('🌐 Connected network:', this.network);
    return this.network;
  }

  getTokensForCurrentChain() {
    const chainId = this.network?.chainId;
    console.log('🔗 Getting tokens for chain:', chainId);
    const tokens = {};

    // Add native token for this chain
    const nativeConfig = NATIVE_TOKENS[chainId];
    console.log('🪙 Native config for chain', chainId, ':', nativeConfig);
    
    if (nativeConfig) {
      tokens.NATIVE = {
        ...nativeConfig,
        decimals: 18,
        address: 'native',
        isNative: true,
      };
    } else {
      // Fallback for unknown chains - assume ETH-like native
      console.log('⚠️ No native config found, using fallback');
      tokens.NATIVE = {
        name: 'Native Token',
        symbol: 'ETH',
        icon: '💎',
        decimals: 18,
        address: 'native',
        isNative: true,
        coingeckoId: 'ethereum',
      };
    }

    // Add ERC-20 tokens available on this chain
    const erc20Tokens = ERC20_TOKENS_BY_CHAIN[chainId] || {};
    console.log('📄 ERC20 tokens for chain', chainId, ':', Object.keys(erc20Tokens));
    
    for (const [symbol, tokenConfig] of Object.entries(erc20Tokens)) {
      tokens[symbol] = {
        ...tokenConfig,
        isNative: false,
      };
    }

    console.log('📋 Total tokens:', Object.keys(tokens));
    return tokens;
  }

  async getTokenBalance(tokenInfo, userAddress) {
    try {
      if (tokenInfo.isNative) {
        const balance = await this.provider.getBalance(userAddress);
        return ethers.formatEther(balance);
      }

      const tokenContract = new ethers.Contract(
        tokenInfo.address,
        ['function balanceOf(address) view returns (uint256)'],
        this.provider
      );
      const balance = await tokenContract.balanceOf(userAddress);
      return ethers.formatUnits(balance, tokenInfo.decimals);
    } catch (error) {
      console.error(`Failed to get ${tokenInfo.symbol} balance:`, error);
      return '0';
    }
  }

  async getAllBalances(userAddress) {
    const tokens = this.getTokensForCurrentChain();
    const balances = {};

    for (const [key, token] of Object.entries(tokens)) {
      try {
        const rawBalance = await this.getTokenBalance(token, userAddress);
        const formattedBalance = parseFloat(rawBalance);
        balances[key] = {
          ...token,
          balance: formattedBalance,
          hasBalance: formattedBalance > 0,
          error: false,
        };
        console.log(`✅ ${token.symbol}: ${formattedBalance}`);
      } catch (err) {
        console.error(`❌ Failed to get ${token.symbol} balance:`, err);
        balances[key] = {
          ...token,
          balance: 0,
          hasBalance: false,
          error: true,
        };
      }
    }
    return balances;
  }

  async getCryptoPrices() {
    const tokens = this.getTokensForCurrentChain();
    const coingeckoIds = [...new Set(Object.values(tokens).map((t) => t.coingeckoId).filter(Boolean))];
    
    if (coingeckoIds.length === 0) {
      return { NATIVE: 1, USDC: 1, USDT: 1, DAI: 1 };
    }

    try {
      const ids = coingeckoIds.join(',');
      const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`);
      const data = await res.json();
      const prices = {};
      
      for (const [key, token] of Object.entries(tokens)) {
        if (token.coingeckoId && data[token.coingeckoId]) {
          prices[key] = data[token.coingeckoId].usd;
        } else if (['USDC', 'USDT', 'DAI'].includes(token.symbol)) {
          prices[key] = 1.0;
        } else {
          // Fallback prices for native tokens if API fails
          if (token.symbol === 'ETH') prices[key] = 2500;
          else if (token.symbol === 'POL') prices[key] = 0.5;
          else if (token.symbol === 'BNB') prices[key] = 300;
          else if (token.symbol === 'AVAX') prices[key] = 25;
          else prices[key] = 1;
        }
      }
      return prices;
    } catch (err) {
      console.warn('Failed to fetch prices, using defaults:', err);
      const prices = {};
      for (const [key, token] of Object.entries(tokens)) {
        if (['USDC', 'USDT', 'DAI'].includes(token.symbol)) {
          prices[key] = 1.0;
        } else if (token.symbol === 'ETH') {
          prices[key] = 2500;
        } else if (token.symbol === 'POL') {
          prices[key] = 0.5;
        } else if (token.symbol === 'BNB') {
          prices[key] = 300;
        } else if (token.symbol === 'AVAX') {
          prices[key] = 25;
        } else {
          prices[key] = 1;
        }
      }
      return prices;
    }
  }

  calculateRecommendationScore(balance, required, hasEnough) {
    if (!hasEnough) return 0;
    const ratio = balance / required;
    if (ratio >= 1 && ratio <= 2) return 100;
    if (ratio > 2) return 80;
    return 0;
  }

  async getPaymentOptionsWithBalances(usdAmount, userAddress) {
    const [balances, prices] = await Promise.all([
      this.getAllBalances(userAddress),
      this.getCryptoPrices(),
    ]);
    const options = [];
    
    for (const [key, tokenInfo] of Object.entries(balances)) {
      const price = prices[key];
      if (price == null) continue;
      
      const requiredAmount = usdAmount / price;
      const hasEnough = tokenInfo.balance >= requiredAmount;
      const score = this.calculateRecommendationScore(
        tokenInfo.balance,
        requiredAmount,
        hasEnough
      );
      
      // Display decimals based on token type
      const isStable = ['USDC', 'USDT', 'DAI'].includes(tokenInfo.symbol);
      const displayDecimals = isStable ? 2 : tokenInfo.symbol === 'ETH' ? 6 : 4;
      const displaySymbol = tokenInfo.symbol || key;
      
      options.push({
        symbol: key,
        displaySymbol,
        name: tokenInfo.name,
        icon: tokenInfo.icon,
        decimals: tokenInfo.decimals,
        address: tokenInfo.address,
        isNative: !!tokenInfo.isNative,
        balance: tokenInfo.balance,
        balanceUSD: tokenInfo.balance * price,
        hasEnough,
        requiredAmount,
        requiredAmountFormatted: `${requiredAmount.toFixed(displayDecimals)} ${displaySymbol}`,
        rate: price,
        usdValue: usdAmount,
        score,
      });
    }
    
    // Sort: stablecoins with balance first, then by score
    options.sort((a, b) => {
      const aIsStable = ['USDC', 'USDT', 'DAI'].includes(a.displaySymbol);
      const bIsStable = ['USDC', 'USDT', 'DAI'].includes(b.displaySymbol);
      if (a.hasEnough && !b.hasEnough) return -1;
      if (!a.hasEnough && b.hasEnough) return 1;
      if (aIsStable && !bIsStable) return -1;
      if (!aIsStable && bIsStable) return 1;
      return b.score - a.score;
    });
    
    return options;
  }
}

const walletService = new WalletService();
export default walletService;
